import { useEffect, useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useSkillStore, type SkillTemplate } from '@/state/skillStore';
import { useCooldownStore } from '@/state/cooldownStore';
import { getSkillIcon } from '@/data/skillIconRegistry';

export function SkillBar() {
  const ch = useCharacterStore((s) => s.current);
  const templates = useSkillStore((s) => s.templates);
  const fetchSkills = useSkillStore((s) => s.fetch);
  const learned = useSkillStore((s) => s.learned);
  const fetchLearned = useSkillStore((s) => s.fetchLearned);
  const cds = useCooldownStore((s) => s.cds);
  const clearCd = useCooldownStore((s) => s.clear);
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    void fetchSkills();
    let raf = 0;
    const loop = () => {
      setNow(performance.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [fetchSkills]);
  useEffect(() => {
    if (ch) void fetchLearned(ch.id);
  }, [ch, fetchLearned]);
  if (!ch) return null;
  const skills: SkillTemplate[] = templates
    .filter((s) => s.class === ch.class && (learned[s.id] ?? 0) > 0)
    .slice(0, 5);

  return (
    <div style={{ position: 'absolute', left: '50%', bottom: 16, transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
      {skills.map((s, i) => {
        const cd = cds[s.id];
        let pct = 0;
        let remainSec = 0;
        if (cd) {
          const elapsed = now - cd.startedAt;
          const ratio = Math.min(1, Math.max(0, elapsed / cd.durationMs));
          pct = (1 - ratio) * 100;
          remainSec = Math.max(0, (cd.endsAt - now) / 1000);
          if (ratio >= 1) {
            // Defer clear to avoid setState during render
            queueMicrotask(() => clearCd(s.id));
          }
        }
        const onCd = pct > 0;
        const cast = () => {
          const code = `Digit${i + 1}`;
          window.dispatchEvent(new KeyboardEvent('keydown', { key: String(i + 1), code, bubbles: true }));
        };
        return (
          <div
            key={s.id}
            onClick={cast}
            onContextMenu={(e) => { e.preventDefault(); cast(); }}
            style={{ padding: 4, width: 64, height: 64, textAlign: 'center', position: 'relative', overflow: 'hidden', cursor: onCd ? 'not-allowed' : 'pointer', userSelect: 'none', background: 'rgba(10,14,28,0.85)', border: '2px solid #6b5320', borderRadius: 4, boxShadow: 'inset 0 0 8px rgba(0,0,0,0.6)', color: '#f3e7c2' }}
            title={`${s.name}\n${s.manaCost} MP · CD ${(s.cooldownMs / 1000).toFixed(1)}s`}
          >
            {(() => {
              const icon = getSkillIcon(s.code, s.class);
              return icon
                ? <img src={icon} alt={s.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', imageRendering: 'pixelated', display: 'block' }} />
                : <div style={{ fontSize: 22, lineHeight: '56px' }}>✦</div>;
            })()}
            <div style={{ position: 'absolute', top: 2, left: 4, fontSize: 10, color: '#ffd166', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{i + 1}</div>
            <div style={{ position: 'absolute', bottom: 2, right: 4, fontSize: 9, color: '#9ec5fe', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{s.manaCost}MP</div>
            {onCd && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `conic-gradient(rgba(0,0,0,0.65) ${pct}%, rgba(0,0,0,0) ${pct}% 100%)`,
                    pointerEvents: 'none',
                    transition: 'background 80ms linear',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    pointerEvents: 'none',
                  }}
                >
                  {remainSec >= 1 ? Math.ceil(remainSec) : remainSec.toFixed(1)}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
